import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatBRL } from "@/lib/billing";
import { SERVICE_TYPES } from "@/lib/services";
import { useAuth } from "@/context/AuthContext";
import { ServiceTypeDialog } from "@/components/app/ServiceTypeDialog";

const TypeCard = ({ t, canEdit, onEdit, onDelete }) => (
  <div className={`rounded-xl border p-4 ${t.active ? "border-slate-700/60 bg-slate-800/60" : "border-slate-800 bg-slate-900/60 opacity-60"}`} style={{ borderLeft: `3px solid ${t.hex}` }} data-testid={`service-type-card-${t.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate font-semibold">{t.label}</div>
        <div className="mt-1 text-xs text-slate-400">A cada {t.validity} · avisar {t.lead_days} dias antes{t.default_price != null ? ` · ${formatBRL(t.default_price)}` : ""}</div>
      </div>
      {!t.active && <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[11px] text-slate-400">Inativo</span>}
    </div>
    {canEdit && (
      <div className="mt-3 flex gap-1">
        <Button size="sm" variant="ghost" onClick={() => onEdit(t)} data-testid={`service-type-edit-${t.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
        <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => onDelete(t)} data-testid={`service-type-delete-${t.id}`}><Trash2 className="mr-1 h-3.5 w-3.5" /> Excluir</Button>
      </div>
    )}
  </div>
);

export const ServiceTypesView = ({ version, onChanged }) => {
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const canEdit = user?.tenant_role === "owner";
  const types = Object.values(SERVICE_TYPES).sort((a, b) => Number(b.active) - Number(a.active) || a.label.localeCompare(b.label));

  const remove = async (t) => {
    if (!window.confirm(`Excluir "${t.label}" do catálogo?`)) return;
    try {
      await api.delete(`/service-types/${t.id}`);
      toast.success("Serviço excluído.");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section data-testid="service-types-view" data-version={version}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">Catálogo de serviços</h2>
          <p className="text-sm text-slate-400">Cada serviço tem sua periodicidade. O próximo retorno é calculado a partir dela.</p>
        </div>
        {canEdit && <Button onClick={() => setDialog({})} className="rounded-full font-semibold" data-testid="service-type-new-button"><Plus className="mr-1 h-4 w-4" /> Novo serviço</Button>}
      </div>
      {types.length === 0 && <p className="rounded-xl border border-dashed border-slate-700 py-12 text-center text-sm text-slate-500" data-testid="service-types-empty">Nenhum serviço cadastrado. Comece criando os serviços que sua empresa oferece.</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {types.map((t) => <TypeCard key={t.id} t={t} canEdit={canEdit} onEdit={(type) => setDialog({ type })} onDelete={remove} />)}
      </div>
      <ServiceTypeDialog state={dialog} onClose={() => setDialog(null)} onSaved={onChanged} />
    </section>
  );
};
